import React, { useState } from 'react';
import { Route } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { NavBarContent } from 'feature/nav/NavBar';
import DocumentTabs from '../DocumentTabs';
import { StyledContent } from './Document.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useShowChanges } from 'global/hooks/useShowChanges';
import { useFilter } from 'global/hooks/useFilter';
import { first, get } from 'global/lib/lodash';
import { Transform } from '../DocumentList';
import DiffViewer from 'global/components/diff-text';
import withDocumentParent from './withDocumentParent';
import { Button, IconButton } from 'global/components/Button';
import { useHashView } from 'feature/share/useHashView';
import { useEditionAccessReady } from '../EditionAccess';
import ShareLinkModal from 'feature/share/ShareLinkModal';
import SnackAlerts from 'global/components/snack-alerts';
import { useScreen } from 'global/styles';
import { executePrint } from 'global/utils';

export default function Document(props) {
  const { match } = props;
  const { showChangesOn } = useShowChanges();

  const edition_id = get(props, 'editionId', get(match, 'params.library'));
  const classification_id = get(props, 'classificationId', get(match, 'params.document'));
  // PFM-2641: /sharehash/v1/editions/:id 500s, but the sharehash whitelist allows
  // include=edition on the edition-documents show route (scope.id is the shared
  // edition-document id), so a hashed viewer reads the edition through that instead.
  // scorecards.checklists isn't whitelisted and is only needed for edit flows,
  // which a hashed viewer never reaches.
  const { active: hashViewActive, scope: hashScope } = useHashView();
  const useHashEditionRead = hashViewActive && !!get(hashScope, 'id');
  // Library document pages wait until GET /editions/:id?include=realm succeeds.
  // useEffect is required so waitFor flipping true actually issues the request.
  const editionAccessReady = useEditionAccessReady();
  // PFM-2689: in the Account Settings > Content "Add Existing" picker a course /
  // shared source can have no real edition id (edition_id resolves to undefined),
  // which made the normal read hit `/editions/undefined` (404 + CRA error overlay).
  // Gate the read on a usable id for the active path (no-op for normal/hash views).
  const edition = useRetrieveData(
    { url: useHashEditionRead ? `/edition-documents/${hashScope.id}` : `/editions/${edition_id}` },
    {
      build: ['editions'],
      useEffect: true,
      waitFor: editionAccessReady && (useHashEditionRead || !!edition_id),
      params: { include: useHashEditionRead ? 'edition' : 'scorecards,scorecards.checklists' },
      transformData: (d) => first(d),
    }
  );

  const { data, loading, update } = useRetrieveData(
    { url: `/edition-documents` },
    {
      useLangFilter: true,
      build: ['editionDocuments'],
      params: {
        include: 'document',
        filter: { classification_id, edition_id },
        editMode: 1,
      },
      useEffect: true,
      waitFor: editionAccessReady && !!classification_id,
      transformData: Transform,
    }
  );
  
  return <_Document {...props} doc={data} {...{data, edition, edition_id, classification_id, showChangesOn, loading, update }} />
}

const _Document = withDocumentParent((props) => {
  const { data, edition_id, classification_id, edition, editionParent, showChangesOn, parentDocument, documentParentId, loading, update } = props;
  const { active: hashViewActive } = useHashView();
  const { isMobile } = useScreen();
  const { filter, canShowDoc } = useFilter('library.editions');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // PFM-2648: confirmation toast after a Share email is sent (the modal closes
  // on send, so the toast is owned here, not inside ShareLinkModal).
  const [shareSentAlert, setShareSentAlert] = useState();

  // Same Use Location filter as DocumentList; skip for share/hash viewers.
  const documents =
    get(filter, 'useLocation') && !hashViewActive && data ? data.filter((doc) => canShowDoc(doc, edition.data)) : data;

  const showChanges = showChangesOn && !!documentParentId && !parentDocument.loading;
  const diffElement = showChanges ? <DiffViewer oldText={get(parentDocument, 'data.name')} newText={get(data, '0.document.name')} diffType="words" /> : get(data, '0.document.name');

  // PFM-2564: document is identified by edition_id + classification (route /library/:library/document/:document).
  // The hash redemption uses the edition-document id; we approximate with first(data) which carries the joined id.
  const editionDocumentId = get(data, '0.id');
  const documentName = get(data, '0.document.name');
  const [activeDocType, setActiveDocType] = useState();

  const handleActiveDoc = (doc) => {
    const current = Array.isArray(doc) ? first(doc) : doc;
    setActiveDocType(get(current, 'documentableType'));
    props.onDoc && props.onDoc(doc);
  };

  const docType = activeDocType ?? get((documents || []).find((d) => d.primaryDocument) || first(documents), 'documentableType');
  const showShare = !hashViewActive && editionDocumentId;
  const showPrint = showShare && (docType === 'checklist' || docType === 'step_by_step');

  const headerActions = [
    showShare && { testId: 'document-share-btn', onClick: () => setShareModalOpen(true), label: 'Share', icon: 'ios_share' },
    showPrint && { testId: 'document-print-btn', onClick: executePrint, label: 'Print', icon: 'print' },
  ].filter(Boolean);

  const documentActions =
    headerActions.length > 0 ? (
      <>
        {headerActions.map(({ testId, onClick, label, icon }) =>
          isMobile ? (
            <IconButton
              key={testId}
              data-testid={testId}
              onClick={onClick}
              color="inherit"
              aria-label={label}
              toolTip={label}
              icon={icon}
            />
          ) : (
            <Button key={testId} data-testid={testId} onClick={onClick} startIcon={icon}>
              {label}
            </Button>
          )
        )}
      </>
    ) : null;

  return (
    <StyledContent>
      <NavBarContent
        pageTitle={loading ? 'Loading...' : diffElement}
        link={'library/' + edition_id}
        closeProps={props.closeProps}
        actions={documentActions}
      />
      {shareModalOpen && (
        <ShareLinkModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSent={({ message }) => setShareSentAlert({ message })}
          scope={{
            kind: 'document',
            id: editionDocumentId,
            editionId: edition_id,
            classificationId: classification_id || get(data, '0.document.classification.id'),
          }}
          title={documentName || 'document'}
        />
      )}
      <SnackAlerts alerts={shareSentAlert} setAlerts={setShareSentAlert} />
      <Route
        exact
        path={`/library/:library/document/:document`}
        render={(p) => (
          <DocumentTabs
            {...p}
            edition={edition.data}
            editionParent={editionParent.data}
            documents={documents}
            parentDocument={parentDocument}
            documentParentId={documentParentId}
            update={update}
            userCanEdit={get(edition, 'data.userCanEdit', false)}
            onDoc={handleActiveDoc}
          />
        )}
      />
      {props.isModal && (
        <DocumentTabs
          {...props}
          documents={documents}
          edition={edition.data}
          editionParent={editionParent.data}
          update={update}
          canEdit={get(edition, 'data.userCanEdit', false)}
          onDoc={handleActiveDoc}
        />
      )}
      {loading && <LinearProgress />}
    </StyledContent>
  );
});
